/**
 * Base Drawing Controller - Parent class for all drawing controllers
 * Contains common functionality shared across drawing operations
 */
import {Subject} from 'rxjs';

export abstract class BaseDrawController {
  protected map: any;
  protected drawnItems: any;
  protected isDrawingEnabled = false;
  protected editModeEnabled = false;

  // Event handlers stored for cleanup
  protected mapClickHandler: any = null;
  protected mapMouseMoveHandler: any = null;

  // All drawn shapes for edit mode
  protected allDrawnShapes: Array<{
    layer: any;
    markers?: any[];
    labels?: any[];
    controlButtons?: any;
  }> = [];

  // Event emitter for shape creation - to be defined by child classes
  public abstract shapeCreated$: Subject<any>;

  constructor(mapInstance: any) {
    this.map = mapInstance;
  }

  /**
   * Set the drawn items feature group
   */
  public setDrawnItems(items: any): void {
    this.drawnItems = items;
  }

  /**
   * Enable drawing mode - to be implemented by child classes
   */
  public abstract enable(): void;

  /**
   * Disable drawing mode - to be implemented by child classes
   */
  public abstract disable(): void;

  /**
   * Check if drawing is active
   */
  public isActive(): boolean {
    return this.isDrawingEnabled;
  }

  /**
   * Set cursor to crosshair and add drawing mode class
   */
  protected enableDrawingMode(additionalClass?: string): void {
    this.isDrawingEnabled = true;
    this.map.getContainer().style.cursor = 'crosshair';
    this.map.getContainer().classList.add('drawing-mode-active');
    if (additionalClass) {
      this.map.getContainer().classList.add(additionalClass);
    }
  }

  /**
   * Reset cursor and remove drawing mode class
   */
  protected disableDrawingMode(additionalClass?: string): void {
    this.isDrawingEnabled = false;
    this.map.getContainer().style.cursor = '';
    this.map.getContainer().classList.remove('drawing-mode-active');
    if (additionalClass) {
      this.map.getContainer().classList.remove(additionalClass);
    }
  }

  /**
   * Remove event listeners from map
   */
  protected removeMapEventListeners(handlers: { event: string; handler: any }[]): void {
    handlers.forEach(({event, handler}) => {
      if (handler) {
        this.map.off(event, handler);
      }
    });
  }

  /**
   * Remove DOM event listeners
   */
  protected removeDomEventListeners(listeners: { element: any; event: string; handler: any }[]): void {
    listeners.forEach(({element, event, handler}) => {
      if (handler && element) {
        element.removeEventListener(event, handler);
      }
    });
  }

  /**
   * Enable edit mode - shows edit buttons for all drawn shapes
   */
  public enableEditMode(): void {
    if (this.editModeEnabled) return;
    this.editModeEnabled = true;

    // Add control buttons to all shapes
    this.allDrawnShapes.forEach((shape, index) => {
      this.addEditButtons(shape, index);
    });
  }

  /**
   * Disable edit mode - hides all edit buttons
   */
  public disableEditMode(): void {
    if (!this.editModeEnabled) return;
    this.editModeEnabled = false;

    // Remove all control buttons
    this.allDrawnShapes.forEach(shape => {
      this.removeEditButtons(shape);
    });
  }

  /**
   * Add edit buttons (Back and Remove) to a shape
   * COMMENTED OUT - can be overridden by child classes if needed
   */
  protected addEditButtons(shape: any, index: number): void {
    // COMMENTED OUT: Back and Remove button logic
    // Child classes can override this method to implement custom button behavior
  }

  /**
   * Remove edit buttons from a shape
   * COMMENTED OUT - can be overridden by child classes if needed
   */
  protected removeEditButtons(shape: any): void {
    // COMMENTED OUT: Remove button logic
    // Child classes can override this method to implement custom removal behavior
  }

  /**
   * Undo last shape (Back button action)
   * COMMENTED OUT - can be overridden by child classes if needed
   */
  protected undoLastShape(index: number): void {
    // COMMENTED OUT: Back button logic
    // Child classes can override this method to implement undo behavior
  }

  /**
   * Remove a specific shape
   * COMMENTED OUT - can be overridden by child classes if needed
   */
  protected removeShape(index: number): void {
    // COMMENTED OUT: Remove shape logic
    // Child classes can override this method to implement removal behavior
  }

  /**
   * Register a newly drawn shape for edit mode tracking
   */
  public registerDrawnShape(layer: any, markers?: any[], labels?: any[]): void {
    const shape = {
      layer: layer,
      markers: markers || [],
      labels: labels || []
    };

    this.allDrawnShapes.push(shape);

    if (this.editModeEnabled) {
      this.addEditButtons(shape, this.allDrawnShapes.length - 1);
    }
  }

  /**
   * Clear all layers from map
   */
  protected clearLayers(layers: any[]): void {
    layers.forEach(layer => {
      if (layer && this.map.hasLayer(layer)) {
        this.map.removeLayer(layer);
      }
    });
  }

  /**
   * Add layer to drawn items if available
   */
  protected addToDrawnItems(layer: any): void {
    if (this.drawnItems && layer) {
      this.drawnItems.addLayer(layer);
    }
  }

  /**
   * Remove layer from drawn items if available
   */
  protected removeFromDrawnItems(layer: any): void {
    if (this.drawnItems && layer && this.drawnItems.hasLayer(layer)) {
      this.drawnItems.removeLayer(layer);
    }
  }
}

